import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import EasterEggs from '../components/EasterEggs';
import { isSoundEnabled, toggleSound, playSparkle, unlockAudio } from '../utils/sounds';

const roles = [
  { emoji: '🎓', title: 'VIP 1: Kod Kaşifleri', color: '#6366f1' },
  { emoji: '🚀', title: 'VIP 2: Robot Fatihleri', color: '#ec4899' },
  { emoji: '🦸', title: 'VIP 3: Dijital Süper Kahramanlar', color: '#f59e0b' },
];

const features = [
  { emoji: '🎤', text: 'Probleme özel rollerle mülakat simülasyonu' },
  { emoji: '📎', text: 'Fotoğraf, anket, çizim kanıt olarak yükle' },
  { emoji: '🤖', text: 'Yapay zeka sana özel sorular sorar' },
  { emoji: '📜', text: 'Gereken rolleri tamamlayınca PDF diplomanı al' },
];

function useInView<T extends HTMLElement>(threshold = 0.15) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current || inView) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, inView]);
  return { ref, inView };
}

function FeatureCard({ emoji, text, delay }: { emoji: string; text: string; delay: number }) {
  const { ref, inView } = useInView<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={`landing-feature-card ${inView ? 'is-visible' : ''}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <span className="landing-feature-emoji" aria-hidden="true">{emoji}</span>
      <p>{text}</p>
    </div>
  );
}

function Confetti({ trigger }: { trigger: number }) {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!trigger || !containerRef.current) return;
    const colors = ['#6366f1', '#ec4899', '#f59e0b', '#16a34a', '#0891b2', '#a855f7'];
    const shapes = ['●', '■', '▲', '★', '✦'];
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const pieceCount = 36;
    for (let i = 0; i < pieceCount; i++) {
      const piece = document.createElement('span');
      piece.className = 'confetti-piece';
      piece.textContent = shapes[Math.floor(Math.random() * shapes.length)];
      piece.style.color = colors[Math.floor(Math.random() * colors.length)];
      piece.style.left = `${cx}px`;
      piece.style.top = `${cy}px`;
      const angle = (Math.PI * 2 * i) / pieceCount + (Math.random() - 0.5) * 0.4;
      const distance = 90 + Math.random() * 110;
      const dx = Math.cos(angle) * distance;
      const dy = Math.sin(angle) * distance - 60;
      const rot = (Math.random() - 0.5) * 720;
      piece.style.setProperty('--dx', `${dx}px`);
      piece.style.setProperty('--dy', `${dy}px`);
      piece.style.setProperty('--rot', `${rot}deg`);
      piece.style.fontSize = `${12 + Math.random() * 10}px`;
      piece.style.animationDuration = `${800 + Math.random() * 400}ms`;
      container.appendChild(piece);
      setTimeout(() => piece.remove(), 1300);
    }
  }, [trigger]);
  return <div ref={containerRef} className="confetti-layer" aria-hidden="true" />;
}

export default function LandingPage() {
  const [confettiCount, setConfettiCount] = useState(0);
  const [partyMode, setPartyMode] = useState(false);
  const [soundOn, setSoundOn] = useState(isSoundEnabled());

  useEffect(() => {
    if (!partyMode) return;
    const id = window.setTimeout(() => setPartyMode(false), 12000);
    return () => window.clearTimeout(id);
  }, [partyMode]);

  const handleSoundToggle = () => {
    unlockAudio();
    const next = toggleSound();
    setSoundOn(next);
    if (next) playSparkle();
  };

  return (
    <div className={`landing ${partyMode ? 'landing--party' : ''}`}>
      <div className="landing-bg" aria-hidden="true">
        <div className="sun" />
        <div className="cloud cloud-1" />
        <div className="cloud cloud-2" />
        <div className="cloud cloud-3" />
        <div className="blob blob-1" />
        <div className="blob blob-2" />
        <div className="blob blob-3" />
      </div>

      <header className="landing-header">
        <div className="logo-badge landing-header-brand" style={{ fontSize: '1.4rem' }}>
          <span className="brand-emoji" aria-hidden="true">🎓</span>
          <span>Mulakid</span>
        </div>
        <button
          type="button"
          className="sound-toggle"
          onClick={handleSoundToggle}
          aria-label={soundOn ? 'Sesi kapat' : 'Sesi aç'}
          title={soundOn ? 'Sesi kapat' : 'Sesi aç'}
        >
          {soundOn ? '🔊' : '🔇'}
        </button>
      </header>

      <main className="landing-main">
        <section className="landing-hero">
          <div className="landing-anim landing-anim-1" />
          <h1 className="landing-title landing-anim landing-anim-2">
            <span className="wave" aria-hidden="true">👋</span> Hoş Geldin!
            <span className="landing-subtitle">Mülakat Simülasyonuna Hazır mısın?</span>
          </h1>
          <p className="landing-desc landing-anim landing-anim-3">
            Bu proje 3vip öğrencileri için
            tasarlandı. Amacın? Bir problem seç, kanıtlarını hazırla, probleme
            özel rolleri ikna et ve gereken aşamaları geçince diplomanı kap! 🏆
          </p>

          <div className="landing-vip-grid landing-anim landing-anim-4">
            {roles.map((r, idx) => (
              <div
                key={r.title}
                className="landing-vip-card"
                style={{
                  ['--card-color' as string]: r.color,
                  animationDelay: `${idx * 0.12}s`,
                }}
              >
                <span className="vip-emoji" aria-hidden="true">{r.emoji}</span>
                <span>{r.title}</span>
              </div>
            ))}
          </div>

          <div className="cta-wrap landing-anim landing-anim-5">
            <Link
              to="/login"
              className="btn btn--primary btn--lg cta-btn"
              onClick={() => setConfettiCount((c) => c + 1)}
            >
              <span className="cta-sparkle" aria-hidden="true">✨</span>
              <span>🚀 Giriş Yap / Kaydol</span>
              <span className="cta-sparkle" aria-hidden="true">✨</span>
            </Link>
            <Confetti trigger={confettiCount} />
          </div>

          <p className="landing-note landing-anim landing-anim-6">
            * Hesabın yoksa öğretmeninden alacağın kullanıcı adı ve şifreyle giriş yapabilirsin.
          </p>
        </section>

        <section className="landing-features">
          <h2 className="section-title-on-dark">Bu Platformda Neler Var? 🤔</h2>
          <div className="landing-feature-grid">
            {features.map((f, i) => (
              <FeatureCard key={f.text} emoji={f.emoji} text={f.text} delay={i * 90} />
            ))}
          </div>
        </section>

        <section className="landing-faq">
          <h2 className="section-title-on-dark">Sıkça Sorulanlar (Belki) 🤷</h2>
          <div className="landing-faq-list">
            <details className="faq-item">
              <summary>
                <span>Bu ne işe yarıyor?</span>
                <span className="faq-chevron" aria-hidden="true">▾</span>
              </summary>
              <p>Bir okul problemi seçiyorsun (örneğin "kantin daha sağlıklı olsun"), kanıtlarını hazırlıyor, Okul Müdürü'nden Kantinci'ye kadar herkesi ikna etmeye çalışıyorsun. Geçersen diplomayı alıyorsun. Basit. 😎</p>
            </details>
            <details className="faq-item">
              <summary>
                <span>Yapay zeka bana kızar mı?</span>
                <span className="faq-chevron" aria-hidden="true">▾</span>
              </summary>
              <p>Kızmaz ama ikna edemezsen "Bu kanıt yetersiz" der. Üzülme, tekrar deneyebilirsin! 🤗</p>
            </details>
            <details className="faq-item">
              <summary>
                <span>Diplomayı alınca ne oluyor?</span>
                <span className="faq-chevron" aria-hidden="true">▾</span>
              </summary>
              <p>PDF olarak indirebilirsin, duvarına asarsın, anneni gururlandırırsın. 🏅</p>
            </details>
          </div>
        </section>
      </main>

      <footer className="landing-footer">
        <p>
          🎓 Mulakid — <a href="https://www.alpersah.com" target="_blank" rel="noopener noreferrer" className="footer-personal-link">Alper Şahin</a> tarafından öğrencileri için yapıldı © 2026
        </p>
        <p className="landing-footer-small">
          (Kişisel bir proje, kurumsal değildir.)
          {' · '}
          <button
            type="button"
            className="footer-party-btn"
            onClick={() => setPartyMode(true)}
            aria-label="Parti modu aç"
          >
            🎉 Parti Modu
          </button>
          {' · '}
          <span className="footer-hint">↑↑↓↓←→←→BA gizli güçleri açar</span>
        </p>
      </footer>

      <EasterEggs partyMode={partyMode} />
    </div>
  );
}